import { HowTo } from '../types';

export const attendanceHowTos: HowTo[] = [
  {
    key: 'attendance.remove-record',
    version: 1,
    section: 'Attendance',
    title: 'Correct or remove an attendance record',
    summary:
      'Amend a recorded attendance, or clear it altogether — to correct it, or to free the session up so it can be changed or cancelled.',
    youNeed: ['An attendance record already saved against the session'],
    keywords: [
      'attendance', 'record', 'remove', 'delete', 'clear', 'undo', 'logged',
      'recorded', 'blocked', 'wrong', 'mistake', 'correction', 'unlock',
      'amend', 'edit', 'change', 'fix', 'wrong time', 'adjust',
      'cancel', 'change session',
    ],
    routes: ['/attendance'],
    audience: 'staff',
    steps: [
      {
        text: 'Open **Attendance** from the menu.',
        target: 'nav.attendance',
        route: '/attendance',
        navigateOnly: true,
      },
      {
        text: 'Use the arrows to move to the week holding the record.',
        target: 'period-nav',
      },
      {
        text: "Click the session. Its details open on the right. There's no need to unlock the grid — the padlock only guards empty cells.",
        target: 'attendance.grid',
      },
      {
        // People arrive here from "wrong" and "mistake", which this guide owns
        // in its keywords — and most of them want to amend, not delete. Said
        // before the delete step rather than after it.
        text: 'If the record is merely **wrong** rather than unwanted, stop here: change the **Arrival** or **Departure** time, or the comment, then **Save changes**.',
        target: 'attendance.save',
      },
      {
        text: 'Deleting is for when there should be no record at all, or to free the session — the schedule refuses to change one with attendance against it. Click **Delete attendance**.',
        target: 'attendance.delete',
      },
      {
        text: 'Confirm. The recorded times are deleted and the session goes back to a pending placeholder, ready to be recorded again.',
      },
      {
        text: 'The session can now be changed or cancelled on **Weekly Schedule**, which is blocked while attendance is recorded against it.',
      },
    ],
    related: ['schedule.change-session-times'],
  },
  {
    key: 'attendance.record-today',
    version: 1,
    section: 'Attendance',
    title: "Record today's attendance",
    summary:
      'Mark who came and who did not — including absences and their reasons — for every session scheduled today.',
    youNeed: ["Sessions in today's schedule"],
    keywords: [
      'attendance', 'record', 'mark', 'register', 'today', 'daily', 'present',
      'attended', 'arrival', 'departure', 'late', 'times', 'pending',
      'register children', 'take the register',
      // Absence lives in this How To too — there is no separate one.
      'absent', 'absence', 'no show', 'no-show', 'missing', 'did not attend',
      'not here', 'away', 'reason',
      // The app's own preset reasons, because that is the wording people saw.
      'sick', 'illness', 'unwell', 'holiday', 'family', 'appointment',
      'transport', 'unauthorised',
    ],
    routes: ['/attendance'],
    audience: 'staff',
    steps: [
      {
        text: 'Open **Attendance** from the menu. It opens on this week.',
        target: 'nav.attendance',
        route: '/attendance',
        navigateOnly: true,
      },
      {
        text: 'Use the arrows if you need a different week. Today sits in the current one.',
        target: 'period-nav',
      },
      {
        text: "Click **Pending** to show only the sessions with no record yet. That is your list — work it down until it is empty.",
        target: 'attendance.status-chips',
      },
      {
        text: 'Unlock the grid with the padlock. Locked, you can still open a session; unlocked, you also get the quick options on each one.',
        target: 'attendance.grid-lock',
      },
      {
        text: 'With the grid unlocked, click a session to record it as attended at its scheduled times. For anyone who came and went on time, that is the whole job.',
        target: 'attendance.grid',
      },
      {
        text: 'The **⋯** menu records it the same way, and can mark a **no-show** with a reason without opening the panel. Hover a session to bring it up.',
        target: 'attendance.grid',
      },
      {
        text: 'Choose **Attended** or **No-show**.',
        target: 'attendance.status',
      },
      {
        text: 'Change anything that was not as scheduled. **Arrival** and **Departure** start at the scheduled times, and a difference is flagged beside the label. For a no-show, pick a **Reason**.',
      },
      {
        text: 'Click **Save changes**, then move to the next session on the list.',
        target: 'attendance.save',
      },
    ],
    // The dashboard is the other route to the same job — cards rather than a
    // grid — and people search from wherever they are standing.
    related: ['dashboard.sign-in-out', 'attendance.remove-record'],
  },
];
